import React, { useState, useEffect, useRef } from 'react';
import {
  Scale, Building2, Wrench, Car, Truck, Phone, Mail, Globe, MapPin, Star,
  Search, Filter, ChevronDown, ChevronUp, ExternalLink, CheckCircle,
  RefreshCw, ArrowLeft, Award, Sparkles, Send, Shield, Briefcase, Clock,
  GraduationCap, BookOpen, Calculator, FlaskConical, Languages, PenTool,
  School, Shirt, ShoppingBag, Users, Flag
} from 'lucide-react';
import { db, listingTiers, supabase, adminNotifications } from '../lib/supabase';
import { trackDirectoryView, trackDirectoryClick } from '../lib/analytics';
import { TopBanner, BottomBanner } from './BannerAd';

// ==================== SERVICE CATEGORIES ====================
const SERVICE_CATEGORIES = [
  { id: 'all', label: 'All Services', icon: Star, gradient: 'from-gray-600 to-gray-700', bg: 'gray' },
  { id: 'tutor', label: 'General Tutors', icon: GraduationCap, gradient: 'from-brand-500 to-brand-600', bg: 'emerald' },
  { id: 'math_tutor', label: 'Math Tutors', icon: Calculator, gradient: 'from-blue-500 to-blue-600', bg: 'blue' },
  { id: 'science_tutor', label: 'Science Tutors', icon: FlaskConical, gradient: 'from-purple-500 to-purple-600', bg: 'purple' },
  { id: 'language_tutor', label: 'Language Tutors', icon: Languages, gradient: 'from-pink-500 to-pink-600', bg: 'pink' },
  { id: 'test_prep', label: 'Exam Prep', icon: PenTool, gradient: 'from-orange-500 to-orange-600', bg: 'orange' },
  { id: 'homework_helper', label: 'Homework Helpers', icon: BookOpen, gradient: 'from-brand-500 to-brand-600', bg: 'teal' },
  { id: 'consultant', label: 'Education Consultants', icon: Users, gradient: 'from-indigo-500 to-indigo-600', bg: 'indigo' },
  { id: 'school_supplies', label: 'School Supplies', icon: ShoppingBag, gradient: 'from-cyan-500 to-cyan-600', bg: 'cyan' },
  { id: 'bookshop', label: 'Bookshops', icon: BookOpen, gradient: 'from-lime-500 to-lime-600', bg: 'lime' },
  { id: 'uniforms', label: 'Uniforms', icon: Shirt, gradient: 'from-amber-500 to-amber-600', bg: 'amber' },
  { id: 'private_school', label: 'Private Schools', icon: School, gradient: 'from-red-500 to-red-600', bg: 'red' }
];

// ==================== PARISHES ====================
const PARISHES = [
  'All Parishes', 'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

// ==================== CATEGORY CARD COMPONENT ====================
const CategoryCard = ({ category, isActive, onClick, count }) => {
  const Icon = category.icon;

  return (
    <button
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl p-4 text-left transition-all transform hover:scale-105 hover:shadow-xl ${
        isActive
          ? `bg-gradient-to-br ${category.gradient} text-white shadow-lg ring-4 ring-white/50`
          : 'bg-white text-gray-700 border-2 border-gray-100 hover:border-gray-200'
      }`}
    >
      {isActive && (
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-white rounded-full blur-xl" />
        </div>
      )}
      <div className="relative">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
          isActive ? 'bg-white/20' : `bg-${category.bg}-100`
        }`}>
          <Icon className={`w-6 h-6 ${isActive ? 'text-white' : `text-${category.bg}-600`}`} />
        </div>
        <h3 className={`font-bold text-sm ${isActive ? 'text-white' : 'text-gray-800'}`}>{category.label}</h3>
        {count !== undefined && (
          <p className={`text-xs mt-1 ${isActive ? 'text-white/80' : 'text-gray-500'}`}>{count} providers</p>
        )}
      </div>
    </button>
  );
};

// ==================== STAR RATING COMPONENTS ====================
const StarRating = ({ rating, size = 'sm' }) => {
  const sz = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`${sz} ${i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} />
      ))}
    </div>
  );
};

const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[1,2,3,4,5].map(i => (
      <button key={i} type="button" onClick={() => onChange(i)}
        className={`w-9 h-9 transition-colors ${i <= value ? 'text-amber-400' : 'text-gray-300 hover:text-amber-300'}`}>
        <Star className={`w-9 h-9 ${i <= value ? 'fill-amber-400' : ''}`} />
      </button>
    ))}
  </div>
);

// ==================== PROVIDER CARD COMPONENT ====================
const ProviderCard = ({ provider, catConfig, isExpanded, onToggle, onContactClick, onReport, onWriteReview, ratingSummary, providerReviews, getTierBadge }) => {
  const isPremium = provider.tier === 'premium';
  const isBasic = provider.tier === 'basic';
  const isPaid = isPremium || isBasic;

  // Color configurations for each category
  const colorConfig = {
    tutor: { header: 'from-brand-500 to-brand-600', light: 'emerald-50', text: 'emerald-600', badge: 'emerald', accent: 'emerald' },
    math_tutor: { header: 'from-blue-500 to-blue-600', light: 'blue-50', text: 'blue-600', badge: 'blue', accent: 'blue' },
    science_tutor: { header: 'from-purple-500 to-purple-600', light: 'purple-50', text: 'purple-600', badge: 'purple', accent: 'purple' },
    language_tutor: { header: 'from-pink-500 to-pink-600', light: 'pink-50', text: 'pink-600', badge: 'pink', accent: 'pink' },
    test_prep: { header: 'from-orange-500 to-orange-600', light: 'orange-50', text: 'orange-600', badge: 'orange', accent: 'orange' },
    consultant: { header: 'from-indigo-500 to-indigo-600', light: 'indigo-50', text: 'indigo-600', badge: 'indigo', accent: 'indigo' },
    school_supplies: { header: 'from-cyan-500 to-cyan-600', light: 'cyan-50', text: 'cyan-600', badge: 'cyan', accent: 'cyan' },
    uniforms: { header: 'from-amber-500 to-amber-600', light: 'amber-50', text: 'amber-600', badge: 'amber', accent: 'amber' },
    private_school: { header: 'from-red-500 to-red-600', light: 'red-50', text: 'red-600', badge: 'red', accent: 'red' }
  };

  const colors = colorConfig[provider.type] || colorConfig.tutor;

  // Get Google Maps URL
  const getGoogleMapsUrl = () => {
    const address = provider.address || '';
    const parish = provider.parish || '';
    const query = `${address}${address && parish ? ', ' : ''}${parish}${address || parish ? ', Jamaica' : ''}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  };

  return (
    <div className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 ${
      isPremium
        ? 'ring-2 ring-amber-400 shadow-lg shadow-amber-100 hover:shadow-xl hover:shadow-amber-200'
        : isBasic
          ? 'ring-1 ring-blue-200 shadow-md hover:shadow-lg'
          : 'border border-gray-200 shadow-sm hover:shadow-md'
    }`}>

      {/* Premium Header Banner */}
      {isPremium && (
        <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 px-4 py-1.5 flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-800" />
          <span className="text-xs font-bold text-amber-900 uppercase tracking-wider">Featured Business</span>
          <Sparkles className="w-4 h-4 text-amber-800" />
        </div>
      )}

      {/* Category Color Strip */}
      <div className={`h-1 bg-gradient-to-r ${colors.header}`} />

      {/* Main Card Header - Clickable */}
      <div onClick={onToggle} className="p-4 cursor-pointer hover:bg-gray-50 transition-colors">
        <div className="flex items-start gap-4">
          {/* Logo/Icon */}
          <div className={`relative flex-shrink-0 ${isPremium ? 'w-16 h-16' : 'w-14 h-14'}`}>
            <div className={`w-full h-full bg-gradient-to-br ${colors.header} rounded-xl flex items-center justify-center shadow-lg`}>
              {provider.logo_url || provider.image_url ? (
                <img
                  src={provider.logo_url || provider.image_url}
                  alt={provider.name}
                  className={`${isPremium ? 'w-14 h-14' : 'w-12 h-12'} rounded-lg object-cover`}
                />
              ) : (
                <catConfig.icon className={`${isPremium ? 'w-8 h-8' : 'w-6 h-6'} text-white`} />
              )}
            </div>
            {provider.is_verified && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center border-2 border-white shadow">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Business Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className={`font-bold text-gray-900 leading-tight ${isPremium ? 'text-xl' : 'text-lg'}`}>
                  {provider.name}
                </h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-${colors.badge}-100 text-${colors.badge}-700`}>
                    {catConfig.label}
                  </span>
                  {isPremium && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border border-amber-200">
                      <Award className="w-3 h-3" />Premium
                    </span>
                  )}
                  {isBasic && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                      Basic
                    </span>
                  )}
                  {provider.is_verified && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-100 text-brand-700">
                      <Shield className="w-3 h-3" />Verified
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={(e) => { e.stopPropagation(); onReport(provider); }}
                  className="p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                  title="Report this provider"
                >
                  <Flag className="w-4 h-4" />
                </button>
                <ChevronDown className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
              </div>
            </div>
            {ratingSummary && ratingSummary.count > 0 && (
              <div className="flex items-center gap-1.5 mt-1">
                <StarRating rating={ratingSummary.total / ratingSummary.count} />
                <span className="text-xs text-gray-500">
                  {(ratingSummary.total / ratingSummary.count).toFixed(1)} ({ratingSummary.count} review{ratingSummary.count !== 1 ? 's' : ''})
                </span>
              </div>
            )}

            {/* Preview Info */}
            {provider.description && (
              <p className="text-gray-600 text-sm mt-2 line-clamp-2">{provider.description}</p>
            )}

            {/* Quick Contact Preview */}
            {!isExpanded && (provider.phone || provider.parish) && (
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                {provider.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />{provider.phone}
                  </span>
                )}
                {(provider.parish || provider.parishes?.length > 0) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {provider.parishes?.length > 0 ? provider.parishes[0] : provider.parish}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content - Full Business Profile */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* About Section */}
          {provider.description && (
            <div className={`px-5 py-4 ${isPremium ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : isBasic ? 'bg-blue-50' : 'bg-gray-50'}`}>
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />About This Business
              </h4>
              <p className="text-gray-700 leading-relaxed">{provider.description}</p>
            </div>
          )}

          {/* Contact Information Grid */}
          <div className="px-5 py-4">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4" />Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Contact Person */}
              {provider.contact_name && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contact Person</p>
                  <p className="text-gray-900 font-semibold text-lg">{provider.contact_name}</p>
                </div>
              )}

              {/* Phone */}
              {provider.phone && (
                <div className="bg-brand-50 rounded-xl p-4 border border-brand-100 hover:border-brand-300 transition-colors cursor-pointer group"
                     onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'phone', provider.phone); }}>
                  <p className="text-xs font-semibold text-brand-600 uppercase tracking-wide mb-1">Phone Number</p>
                  <p className="text-brand-700 font-bold text-lg flex items-center gap-2 group-hover:text-brand-800">
                    <Phone className="w-5 h-5" />
                    {provider.phone}
                  </p>
                  <p className="text-xs text-brand-600 mt-1">Click to call</p>
                </div>
              )}

              {/* Email */}
              {provider.email && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 hover:border-blue-300 transition-colors cursor-pointer group"
                     onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'email', provider.email); }}>
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Email Address</p>
                  <p className="text-blue-700 font-semibold flex items-center gap-2 group-hover:text-blue-800 break-all">
                    <Mail className="w-5 h-5 flex-shrink-0" />
                    {provider.email}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">Click to send email</p>
                </div>
              )}

              {/* Website */}
              {provider.website && (
                <div className="bg-violet-50 rounded-xl p-4 border border-violet-100 hover:border-violet-300 transition-colors cursor-pointer group"
                     onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'website', provider.website); }}>
                  <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-1">Website</p>
                  <p className="text-violet-700 font-semibold flex items-center gap-2 group-hover:text-violet-800 break-all">
                    <Globe className="w-5 h-5 flex-shrink-0" />
                    {provider.website}
                  </p>
                  <p className="text-xs text-violet-600 mt-1">Click to visit</p>
                </div>
              )}
            </div>
          </div>

          {/* Location Section */}
          {(provider.parish || provider.parishes?.length > 0 || provider.address) && (
            <div className="px-5 py-4 border-t border-gray-100">
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />Location
              </h4>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                {/* Parish */}
                {(provider.parish || provider.parishes?.length > 0) && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Parish</p>
                    <p className="text-gray-900 font-semibold text-lg flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      {provider.parishes?.length > 0 ? provider.parishes.join(', ') : provider.parish}
                    </p>
                  </div>
                )}

                {/* Address */}
                {provider.address && (
                  <div className={provider.parish || provider.parishes?.length > 0 ? 'pt-3 border-t border-gray-200' : ''}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Street Address</p>
                    <p className="text-gray-800 mb-3">{provider.address}</p>
                    <a
                      href={getGoogleMapsUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View on Google Maps
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Services Section */}
          {(provider.services?.length > 0 || provider.specialties?.length > 0) && (
            <div className="px-5 py-4 border-t border-gray-100">
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Star className="w-4 h-4" />Services Offered
              </h4>
              <div className="flex flex-wrap gap-2">
                {(provider.services || provider.specialties || []).map((service, idx) => (
                  <span
                    key={idx}
                    className={`px-4 py-2 rounded-full text-sm font-medium border ${
                      isPremium
                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                        : `bg-${colors.badge}-50 border-${colors.badge}-200 text-${colors.badge}-700`
                    }`}
                  >
                    {typeof service === 'object' ? (service.label || service.value || '') : service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Reviews Section */}
          <div className="px-5 py-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400" />Reviews
              </h4>
              <button
                onClick={(e) => { e.stopPropagation(); onWriteReview(provider); }}
                className="text-xs font-semibold text-brand-600 hover:text-brand-700 transition-colors"
              >
                + Write a Review
              </button>
            </div>
            {(!providerReviews || providerReviews.length === 0) ? (
              <p className="text-sm text-gray-400 italic">No reviews yet — be the first!</p>
            ) : (
              <div className="space-y-3">
                {providerReviews.map(review => (
                  <div key={review.id} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <StarRating rating={review.rating} />
                        {review.title && <p className="font-semibold text-gray-800 text-sm mt-1">{review.title}</p>}
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {new Date(review.created_at).toLocaleDateString('en-JM', { month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{review.body}</p>
                    <p className="text-xs text-gray-400 mt-1.5">— {review.reviewer_name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Call to Action Buttons */}
          <div className={`px-5 py-4 border-t ${isPremium ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : 'bg-gray-50'}`}>
            <div className="flex flex-wrap gap-3">
              {provider.phone && (
                <button
                  onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'phone', provider.phone); }}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-brand-500 to-brand-600 text-white rounded-xl font-bold hover:from-brand-600 hover:to-brand-700 shadow-lg shadow-brand-200 transition-all"
                >
                  <Phone className="w-5 h-5" />Call Now
                </button>
              )}
              {provider.email && (
                <button
                  onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'email', provider.email); }}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-200 transition-all"
                >
                  <Mail className="w-5 h-5" />Send Email
                </button>
              )}
              {provider.website && (
                <button
                  onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'website', provider.website); }}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-violet-500 to-violet-600 text-white rounded-xl font-bold hover:from-violet-600 hover:to-violet-700 shadow-lg shadow-violet-200 transition-all"
                >
                  <Globe className="w-5 h-5" />Visit Website
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== SERVICE PROVIDERS PAGE ====================
export const ServiceProvidersPage = ({ setPage, goBack, lang }) => {
  const [category, setCategory] = useState('all');
  const [parish, setParish] = useState('All Parishes');
  const [search, setSearch] = useState('');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [reportModal, setReportModal] = useState(null); // { provider }
  const [reportForm, setReportForm] = useState({ reason: '', description: '', email: '' });
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [reviewSummary, setReviewSummary] = useState({}); // { [providerId]: { total, count } }
  const [providerReviews, setProviderReviews] = useState({}); // { [providerId]: review[] }
  const [reviewModal, setReviewModal] = useState(null); // provider object
  const [reviewForm, setReviewForm] = useState({ rating: 0, name: '', email: '', title: '', body: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const resultsRef = useRef(null);

  const handleReport = (provider) => {
    setReportModal({ provider });
    setReportForm({ reason: '', description: '', email: '' });
    setReportSuccess(false);
  };

  const submitReport = async () => {
    if (!reportForm.reason || !reportModal) return;
    setReportSubmitting(true);
    try {
      const { error } = await supabase.from('service_provider_reports').insert({
        provider_id: reportModal.provider.id,
        reporter_email: reportForm.email || null,
        reason: reportForm.reason,
        description: reportForm.description || null
      });
      if (error) throw error;
      const reportNotification = {
        type: 'provider_report',
        title: 'Provider Report Submitted',
        message: `A report was filed against ${reportModal.provider.name}`,
        data: {
          provider_name: reportModal.provider.name,
          provider_id: reportModal.provider.id,
          reason: reportForm.reason,
          description: reportForm.description,
          reporter_email: reportForm.email
        }
      };
      adminNotifications.create(reportNotification).catch(() => {});
      adminNotifications.sendEmail(reportNotification).catch(() => {});
      setReportSuccess(true);
    } catch (err) {
      console.error('Report submission failed:', err);
    } finally {
      setReportSubmitting(false);
    }
  };

  useEffect(() => {
    loadTiers();
  }, []);

  useEffect(() => {
    loadProviders();
  }, [category]);

  const loadTiers = async () => {
    try {
      const { data, error } = await listingTiers.getAll();
      if (error) console.error('Error loading tiers:', error);
      setTiers(data || []);
    } catch (error) {
      console.error('Error loading tiers:', error);
    }
  };

  const loadProviders = async () => {
    setLoading(true);

    // Safety timeout - ensure loading stops after 10 seconds max
    const timeoutId = setTimeout(() => {
      console.warn('Provider loading timeout - stopping spinner');
      setLoading(false);
    }, 10000);

    try {
      let allProviders = [];

      // All provider types are now in the unified service_providers table
      if (category === 'all') {
        // Load all service providers
        const { data, error } = await db.serviceProviders.getAll();
        if (error) console.error('Error loading service providers:', error);
        allProviders = data || [];
      } else {
        // Load specific type from service_providers
        const { data, error } = await db.serviceProviders.getByType(category);
        if (error) console.error(`Error loading ${category}:`, error);
        allProviders = data || [];
      }

      // Sort: premium first, then verified, then alphabetically
      allProviders.sort((a, b) => {
        // Premium tier first, then basic, then free
        const tierOrder = { premium: 0, basic: 1, free: 2 };
        const tierA = tierOrder[a.tier] ?? 2;
        const tierB = tierOrder[b.tier] ?? 2;
        if (tierA !== tierB) return tierA - tierB;

        // Then verified
        if (a.is_verified !== b.is_verified) return b.is_verified ? 1 : -1;

        // Then alphabetically
        return (a.name || '').localeCompare(b.name || '');
      });

      setProviders(allProviders);

      // Fetch rating summaries — non-critical, failure must not affect main content
      if (allProviders.length > 0) {
        try {
          const ids = allProviders.map(p => p.id);
          const { data: reviewData } = await db.reviews.getSummaryForProviders(ids);
          if (reviewData) {
            const summary = {};
            reviewData.forEach(r => {
              if (!summary[r.provider_id]) summary[r.provider_id] = { total: 0, count: 0 };
              summary[r.provider_id].total += r.rating;
              summary[r.provider_id].count += 1;
            });
            setReviewSummary(summary);
          }
        } catch {
          // table may not exist yet — silently ignore
        }
      }
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  // Filter providers
  const filteredProviders = providers.filter(p => {
    // Category filter (already applied in loadProviders for single category)
    if (category !== 'all' && p.type !== category) return false;

    // Parish filter
    if (parish !== 'All Parishes') {
      const parishes = p.parishes || (p.parish ? [p.parish] : []);
      if (!parishes.some(pp => pp.toLowerCase().includes(parish.toLowerCase()))) return false;
    }

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        p.name?.toLowerCase().includes(searchLower) ||
        p.firm?.toLowerCase().includes(searchLower) ||
        p.description?.toLowerCase().includes(searchLower) ||
        (p.services || []).some(s => s.toLowerCase().includes(searchLower))
      );
    }

    return true;
  });

  // Get counts per category
  const getCategoryCount = (catId) => {
    if (catId === 'all') return providers.length;
    return providers.filter(p => p.type === catId).length;
  };

  const handleProviderClick = (provider) => {
    trackDirectoryView(provider.type, provider.id, provider.name, provider.parish);
    setExpandedId(prev => prev === provider.id ? null : provider.id);
  };

  useEffect(() => {
    if (expandedId && !providerReviews[expandedId]) {
      db.reviews.getByProvider(expandedId)
        .then(({ data }) => setProviderReviews(prev => ({ ...prev, [expandedId]: data || [] })))
        .catch(() => setProviderReviews(prev => ({ ...prev, [expandedId]: [] })));
    }
  }, [expandedId]);

  const handleWriteReview = (provider) => {
    setReviewModal(provider);
    setReviewForm({ rating: 0, name: '', email: '', title: '', body: '' });
    setReviewSuccess(false);
  };

  const submitReview = async () => {
    if (!reviewForm.rating || !reviewForm.name || !reviewForm.body || !reviewModal) return;
    setReviewSubmitting(true);
    try {
      const { error } = await db.reviews.create({
        provider_id: reviewModal.id,
        reviewer_name: reviewForm.name,
        reviewer_email: reviewForm.email || null,
        rating: reviewForm.rating,
        title: reviewForm.title || null,
        body: reviewForm.body,
      });
      if (error) throw error;
      const reviewNotification = {
        type: 'provider_review',
        title: `New Review: ${reviewModal.name} (${reviewForm.rating}★)`,
        message: `${reviewForm.name} submitted a review for ${reviewModal.name}`,
        data: {
          provider_name: reviewModal.name,
          provider_id: reviewModal.id,
          reviewer_name: reviewForm.name,
          rating: reviewForm.rating,
          title: reviewForm.title,
          body: reviewForm.body,
        }
      };
      adminNotifications.create(reviewNotification).catch(() => {});
      await adminNotifications.sendEmail(reviewNotification);
      setReviewSuccess(true);
    } catch (err) {
      console.error('Review submit error:', err);
      alert('Failed to submit review. Please try again.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleContactClick = (provider, type, value) => {
    trackDirectoryClick(provider.type, provider.id, provider.name, type, provider.parish);
    if (type === 'phone') window.location.href = `tel:${value}`;
    else if (type === 'email') window.location.href = `mailto:${value}`;
    else if (type === 'website') window.open(value, '_blank');
  };

  const getCategoryConfig = (type) => {
    return SERVICE_CATEGORIES.find(c => c.id === type) || SERVICE_CATEGORIES[0];
  };

  const handleCategorySelect = (catId) => {
    setCategory(catId);
    // Scroll to results after a short delay to allow state update
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'premium':
        return <span className="px-2.5 py-1 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-sm"><Award className="w-3 h-3" />Premium</span>;
      case 'basic':
        return <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">Basic</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={() => goBack ? goBack() : setPage('home')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium">
        <ArrowLeft className="w-5 h-5" />Back
      </button>

      {/* Top Banner Ad */}
      <TopBanner page="services" />

      {/* Hero Header */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-800 via-brand-700 to-brand-600 text-white p-8 shadow-2xl">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-300 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-300 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Building2 className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-3xl font-black">Service Providers</h2>
              <p className="text-brand-200">Find trusted professionals across Jamaica</p>
            </div>
          </div>
          <p className="text-brand-100 max-w-2xl">
            Browse our directory of verified tutors, exam prep centres, school supplies, and education services.
            All providers are vetted to ensure quality service for Jamaican families.
          </p>
        </div>
      </section>

      {/* Category Grid */}
      <section>
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5" /> Browse by Category
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {SERVICE_CATEGORIES.map(cat => (
            <CategoryCard
              key={cat.id}
              category={cat}
              isActive={category === cat.id}
              onClick={() => handleCategorySelect(cat.id)}
              count={getCategoryCount(cat.id)}
            />
          ))}
        </div>
      </section>

      {/* Search & Filters */}
      <section className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, services, specialties..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:outline-none transition-all"
            />
          </div>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-500" />
            <select
              value={parish}
              onChange={(e) => setParish(e.target.value)}
              className="pl-12 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:outline-none appearance-none bg-white font-medium"
            >
              {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Results Header */}
      <div ref={resultsRef} className="flex items-center justify-between scroll-mt-4">
        <div className="flex items-center gap-3">
          <span className="px-4 py-2 bg-brand-100 text-brand-700 rounded-xl font-bold">
            {filteredProviders.length} {filteredProviders.length === 1 ? 'Provider' : 'Providers'}
          </span>
          {category !== 'all' && (
            <span className={`px-3 py-1.5 bg-gradient-to-r ${getCategoryConfig(category).gradient} text-white rounded-lg text-sm font-medium`}>
              {getCategoryConfig(category).label}
            </span>
          )}
        </div>
        {parish !== 'All Parishes' && (
          <span className="text-sm text-gray-500 flex items-center gap-1">
            <MapPin className="w-4 h-4" /> {parish}
          </span>
        )}
      </div>

      {/* Providers List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl">
          <RefreshCw className="w-12 h-12 text-brand-500 animate-spin mb-4" />
          <p className="text-gray-500 font-medium">Loading providers...</p>
        </div>
      ) : filteredProviders.length === 0 ? (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-12 text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-700 mb-2">No Providers Found</h3>
          <p className="text-gray-500 mb-6">Try adjusting your filters or search term</p>
          <button onClick={() => setPage('getlisted')} className="px-6 py-3 bg-gradient-to-r from-brand-500 to-brand-600 text-white font-bold rounded-xl hover:from-brand-600 hover:to-brand-700 shadow-lg shadow-brand-200">
            <Send className="w-5 h-5 inline mr-2" />Get Your Business Listed
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProviders.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              catConfig={getCategoryConfig(provider.type)}
              isExpanded={expandedId === provider.id}
              onToggle={() => handleProviderClick(provider)}
              onContactClick={handleContactClick}
              onReport={handleReport}
              onWriteReview={handleWriteReview}
              ratingSummary={reviewSummary[provider.id]}
              providerReviews={providerReviews[provider.id]}
              getTierBadge={getTierBadge}
            />
          ))}
        </div>
      )}

      {/* Get Listed CTA */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-700 via-brand-800 to-brand-900 text-white p-8 text-center shadow-hero">
        <div className="absolute inset-0 bg-hero-sheen pointer-events-none" aria-hidden="true" />
        <div className="relative z-10">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8" />
          </div>
          <h3 className="text-2xl font-black mb-2">Are you a service provider?</h3>
          <p className="text-white/85 mb-6 max-w-lg mx-auto">
            Get listed in our directory and reach thousands of Jamaican families looking for trusted education services
          </p>
          <button
            onClick={() => setPage('getlisted')}
            className="px-8 py-4 min-h-[44px] bg-white text-brand-800 font-black rounded-xl hover:bg-brand-50 shadow-xl transition-all duration-200 hover:scale-105 active:scale-[0.98]"
          >
            <Send className="w-5 h-5 inline mr-2" />Get Listed Today
          </button>
        </div>
      </section>

      {/* Bottom Banner Ad */}
      <BottomBanner page="services" />

      {/* ===== REPORT MODAL ===== */}
      {reportModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setReportModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg text-gray-900">Report a Provider</h3>
                <p className="text-sm text-gray-500">{reportModal.provider.name}</p>
              </div>
              <button onClick={() => setReportModal(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronDown className="w-5 h-5 rotate-180" />
              </button>
            </div>

            {reportSuccess ? (
              <div className="p-8 text-center">
                <div className="w-14 h-14 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-7 h-7 text-brand-600" />
                </div>
                <h4 className="font-bold text-lg text-gray-900 mb-2">Report Received</h4>
                <p className="text-gray-500 text-sm mb-6">Thank you — our team will review this report and take appropriate action.</p>
                <button onClick={() => setReportModal(null)} className="px-6 py-2 bg-brand-600 text-white rounded-xl font-semibold">Done</button>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Reason *</label>
                  <select
                    value={reportForm.reason}
                    onChange={e => setReportForm(f => ({ ...f, reason: e.target.value }))}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:outline-none"
                  >
                    <option value="">Select a reason…</option>
                    <option value="fraud">Fraud / Scam</option>
                    <option value="misrepresentation">Misrepresentation of services</option>
                    <option value="inappropriate">Inappropriate behaviour</option>
                    <option value="spam">Spam / Fake listing</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Details</label>
                  <textarea
                    rows={3}
                    value={reportForm.description}
                    onChange={e => setReportForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Please describe what happened…"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Your email (optional)</label>
                  <input
                    type="email"
                    value={reportForm.email}
                    onChange={e => setReportForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="So we can follow up with you"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-red-400 focus:outline-none"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setReportModal(null)} className="flex-1 py-2 border-2 border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button
                    onClick={submitReport}
                    disabled={!reportForm.reason || reportSubmitting}
                    className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold disabled:opacity-50"
                  >
                    {reportSubmitting ? 'Submitting…' : 'Submit Report'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setReviewModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-black text-xl text-gray-900">Write a Review</h3>
              <p className="text-sm text-gray-500 mt-0.5">{reviewModal.name}</p>
            </div>
            {reviewSuccess ? (
              <div className="p-6 text-center">
                <div className="w-14 h-14 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-brand-600" />
                </div>
                <h4 className="font-bold text-lg text-gray-900 mb-1">Thank you!</h4>
                <p className="text-gray-500 text-sm">Your review has been submitted and is pending admin approval.</p>
                <button onClick={() => setReviewModal(null)} className="mt-4 px-6 py-2 bg-brand-600 text-white rounded-xl font-semibold hover:bg-brand-700">Close</button>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Rating <span className="text-red-500">*</span></label>
                  <StarPicker value={reviewForm.rating} onChange={v => setReviewForm(f => ({ ...f, rating: v }))} />
                  {reviewForm.rating > 0 && (
                    <p className="text-xs text-gray-400 mt-1">{['','Poor','Fair','Good','Very Good','Excellent'][reviewForm.rating]}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={reviewForm.name}
                    onChange={e => setReviewForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Jane Smith"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-amber-300 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email <span className="text-xs font-normal text-gray-400">(not shown publicly)</span></label>
                  <input
                    type="email"
                    value={reviewForm.email}
                    onChange={e => setReviewForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-amber-300 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Review Title <span className="text-xs font-normal text-gray-400">(optional)</span></label>
                  <input
                    type="text"
                    value={reviewForm.title}
                    onChange={e => setReviewForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Summarise your experience"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-amber-300 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Your Review <span className="text-red-500">*</span></label>
                  <textarea
                    value={reviewForm.body}
                    onChange={e => setReviewForm(f => ({ ...f, body: e.target.value }))}
                    placeholder="Share your experience with this provider…"
                    rows={4}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm resize-none focus:ring-2 focus:ring-amber-300 focus:border-transparent"
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <button onClick={() => setReviewModal(null)} className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button
                    onClick={submitReview}
                    disabled={!reviewForm.rating || !reviewForm.name || !reviewForm.body || reviewSubmitting}
                    className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold disabled:opacity-50"
                  >
                    {reviewSubmitting ? 'Submitting…' : 'Submit Review'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceProvidersPage;
